import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import l from '../../../lang';
import OnboardingCard from './onboarding-card';
import FirstTimeCards from './first-time-cards';
import ReminderStepTwo from './reminder-step-two';
import ReminderStepTwoLimitReached from './reminder-step-two-limit-reached';
import ProductCards from './product-cards';
import QuickActionsCard from './quick-actions-card';
import Balances from './balances';
import TopDashboardSection from '../../common/top-dashboard-section';
import AlertMessages from '../alert-messages';
import { useDashboard } from '../../../store/dashboard';
import { useHome } from '../../../store/home';
import { DialogSuccessKycStep1 } from './dialog-kyc-step1';
import { useCompanyInfo } from '../../../store/company-information';
import './styles.scss';
import 'animate.css/animate.min.css';
import { useChannelEvent } from '../../../realtime';
import PendingKyc2 from './pending-kyc-2';
import { AuthSelectors } from '../../../store/auth/selectors';
import { canViewBalance } from '../../../store/home/roles';

const Home = () => {
  const { balances, getBalances, shouldAnimationsRun = true, putShouldAnimationsRun } = useHome();
  const { displayMessageKycStep1, setDisplayMessageKycStep1 } = useCompanyInfo();
  const history = useHistory();
  const { userMe, merchantMe, getMerchantMe } = useDashboard();
  const [shouldShowFirstTimeCards, setShouldShowFirstTimeCards] = useState(false);
  const roles = useSelector(AuthSelectors.getRoles);
  const hasBalanceAccess = canViewBalance(roles);

  const { canOperate, kycLevel, kycReview, completedTransactions = 0, createdCheckouts = 0 } = merchantMe || {};
  const { level = 0, maxAmount = 0 } = kycLevel || {};

  useEffect(() => {
    setShouldShowFirstTimeCards(merchantMe?.legalTalentSessionStatus == 'awaiting_client_correction');
  }, [merchantMe?.legalTalentSessionStatus]);

  useChannelEvent({
    channelName: `kyc2-form-${merchantMe?.id}`,
    eventName: 'kyc2-session-rejected',
    listener: () => {
      getMerchantMe();
    },
  });

  useChannelEvent({
    channelName: `kyc2-form-${merchantMe?.id}`,
    eventName: 'kyc2-session-processed',
    listener: () => {
      getMerchantMe();
    },
  });

  useEffect(() => {
    if (hasBalanceAccess) {
      getBalances();
    }
  }, [hasBalanceAccess]);

  useEffect(() => {
    history.replace({ pathname: '/home' });
  }, [history]);

  // Running animations only once
  shouldAnimationsRun && setTimeout(() => putShouldAnimationsRun(false), 1000);

  const isFirstTime = createdCheckouts === 0 && completedTransactions === 0;

  // Limit is reached when the user has reached 90% of the total amount of transactions. (u$s 2.700  of u$s 3.000)
  // Roles without balance access never fetch balances, so the limit cannot be evaluated for them.
  const limitReached = hasBalanceAccess && maxAmount * 0.9 <= (balances?.data?.totalAccumulated || 0);

  return (
    <div className="home-component">
      <TopDashboardSection title={l('home.welcome.title', userMe?.name?.split(' ')[0] || '')} />

      <AlertMessages />
      {!canOperate && <OnboardingCard shouldAnimationsRun={shouldAnimationsRun} />}

      {((canOperate && level < 4) || shouldShowFirstTimeCards || merchantMe?.pendingKyc2Migration === true) &&
        merchantMe?.legalTalentSessionStatus != 'completed' &&
        merchantMe?.kycReviewStatus != 'REJECTED_BY_INDUSTRY' && (
          <FirstTimeCards shouldAnimationsRun={shouldAnimationsRun} maxAmount={maxAmount} />
        )}

      {completedTransactions > 0 && level < 4 && !limitReached && merchantMe?.legalTalentSessionStatus != 'completed' && (
        <ReminderStepTwo />
      )}
      {['completed', 'manual_review'].includes(merchantMe?.legalTalentSessionStatus || '') && <PendingKyc2 />}

      {completedTransactions > 0 && level < 4 && limitReached && <ReminderStepTwoLimitReached />}

      {hasBalanceAccess && <Balances balances={balances} />}

      {canOperate && <QuickActionsCard />}

      {<ProductCards shouldAnimationsRun={shouldAnimationsRun && Boolean(canOperate) && !isFirstTime} />}

      {displayMessageKycStep1 && canOperate && level === 1 && !kycReview && (
        <DialogSuccessKycStep1 onClose={() => setDisplayMessageKycStep1(false)} />
      )}
    </div>
  );
};

export default Home;
